import React from 'react';
import Modal from '../Modal/Modal';
import styles from './ShareModal.scss';
import PageOwnerAvatar from '../PageOwnerAvatar/PageOwnerAvatar';
import {
  useBasePageQuery,
  usePageOwner,
  useNotNullableContext,
} from '../../hooks';
import {
  FacebookShareRound,
  TwitterShareRound,
  EmailShareRound,
  WhatsAppShareRound,
  SMSShareRound,
} from '../SocialButtons';
import { filterByTypename } from '../../utils/graphql';
import { useTranslation } from 'react-i18next';
import { PageMetaContext } from '../../context';
import generateHash from '../../utils/generateHash';
import isMobile from '../../utils/isMobile';

interface ShareModalProps {
  onClose: () => void;
}

export const ShareModal: React.FunctionComponent<ShareModalProps> = ({
  onClose,
}) => {
  const { isPageOwner } = usePageOwner();
  const { data } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });
  const { currentUrl, pageType, pageShortName } = useNotNullableContext(
    PageMetaContext,
  );

  if (!data?.page) {
    return null;
  }

  const charities = filterByTypename(
    data.page.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  const twitterUrl = `${currentUrl}&utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`;
  const twitterMessage = isPageOwner
    ? t('fundraising.shareTwitterPageOwner', {
        charityName,
      })
    : t('fundraising.shareTwitterNonPageOwner', {
        name: data.page.owner?.name,
        charityName,
      });

  const emailUrl = `${currentUrl}?utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
  const emailMessage = isPageOwner
    ? t('fundraising.shareEmailPageOwner', {
        charityName,
        emailUrl,
      })
    : t('fundraising.shareEmailNonPageOwner', {
        name: data.page.owner?.name,
        charityName,
        emailUrl,
      });

  const whatsAppUrl = `${currentUrl}?utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`;
  const smsUrl = `${currentUrl}?utm_source=sms&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-sms&utm_term=${generateHash()}`;
  const whatsappAndSmsMessage = isPageOwner
    ? t('fundraising.shareWhatsappSMSPageOwner', {
        charityName,
      })
    : t('fundraising.shareWhatsappSMSNonPageOwner', {
        name: data.page.owner?.name,
        charityName,
      });

  const isMobileDevice = isMobile(window);

  return (
    <Modal onCloseRequest={onClose} className={styles.modal} displayCloseButton>
      <div className={`row ${styles.topContainer}`}>
        <div className="col-md-3">
          <PageOwnerAvatar size="auto" />
        </div>
        <div className={`col-md-9 ${styles.description}`}>
          <h2
            className={`jg-h2 ${styles.title}`}
          >{`Help ${data.page?.owner?.name}`}</h2>
          <p>
            Sharing this page with your friends could help raise up to 3x more
            in donations
          </p>
        </div>
      </div>
      <ul className={styles.list}>
        <li className={styles.listItem}>
          <FacebookShareRound />
        </li>
        <li className={styles.listItem}>
          <TwitterShareRound message={twitterMessage} url={twitterUrl} />
        </li>
        <li className={styles.listItem}>
          <EmailShareRound
            body={emailMessage}
            subject={t('common.defaultEmailSubject')}
          />
        </li>
        {isMobileDevice && (
          <>
            <li className={styles.listItem}>
              <WhatsAppShareRound
                message={`${whatsappAndSmsMessage} ${whatsAppUrl}`}
              />
            </li>
            <li className={styles.listItem}>
              <SMSShareRound message={`${whatsappAndSmsMessage} ${smsUrl}`} />
            </li>
          </>
        )}
      </ul>
    </Modal>
  );
};
